<template>
  <div class="eventClass">
    <div
      class="py-4 white titleBar"
      id="titleBar"
      :style="{ visibility: showTitleBanner ? 'visible' : 'hidden' }"
    >
      <CustomRow>
        <div class="text-small">
          <v-icon color="black" class="mr-1" dense>mdi-domain</v-icon>
          <template v-for="(host, i) in campaign.hosts">
            {{ host }}
            <span
              :key="`host-${i}`"
              class="grey--text"
              v-if="i !== campaign.hosts.length - 1"
              >、</span
            >
          </template>
        </div>
        <h4 v-if="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm">
          {{ campaign.title }}
          <v-spacer />
        </h4>
        <h1 v-else class="mt-0">
          {{ campaign.title }}
          <v-spacer />
        </h1>
      </CustomRow>
    </div>
    <div class="white">
      <CustomRow>
        <v-row class="pt-6" id="banner">
          <v-col cols="12">
            <div class="image">
              <img :src="`${campaign.picBigActiveMain}`" />
            </div>
          </v-col>
          <v-col cols="12" class="pb-2">
            <div class="mb-0 mt-3 text-small domain">
              <v-icon color="black" class="mr-1" dense>mdi-domain</v-icon>
              <template v-for="(host, i) in campaign.hosts">
                {{ host }}
                <span
                  :key="`hosts-${i}`"
                  class="grey--text"
                  v-if="i !== campaign.hosts.length - 1"
                  >、</span
                >
              </template>
            </div>
            <h1
              class="mt-1 text-xs-page-title text-sm-massive-title is-word-break"
            >
              {{ campaign.title }}
            </h1>
          </v-col>
        </v-row>
      </CustomRow>
    </div>
    <div
      class="white campainTabs"
      :style="{ top: `${appBarHeight + titleBarHeight}px` }"
      id="campainTabs"
    >
      <CustomRow class="px-0 px-md-3">
        <v-tabs v-model="tabSelect" color="primary-1" grow show-arrows>
          <v-tab
            v-for="(tab, tabsIndex) in tabs"
            @click="scrollTo(tab.type, tab)"
            :key="`activity-tab-${tabsIndex}`"
          >
            {{ tab.title }}
          </v-tab>
        </v-tabs>
      </CustomRow>
    </div>

    <CustomRow>
      <CustomCard
        v-if="announcementSection"
        :id="announcementSection.id"
        class="bg-yellow"
      >
        <template v-slot:title>
          <h4>{{ $t(announcementSection.titleKey) }}</h4>
        </template>
        <div
          v-html="announcementSection.html"
          class="campaign-content-frame text-left"
        ></div>
      </CustomCard>
      <CustomCard id="buyTicket" v-if="sessions.length">
        <template v-slot:title>
          <h4 class="mb-3">
            {{ $t(`activity_buy_ticket${lotterySuffix}`) }}
          </h4>
          <div class="text-small grey--text mb-2" v-if="campaign.calendarMode">
            {{ $t("activity_calendar_nearest_month") }}
          </div>
        </template>

        <!-- 月曆模式 -->
        <template v-if="campaign.calendarMode">
          <SessionCalendar
            :sessions="sessions"
            :eventId="campaign.event_id"
            @emitGetSessionStatus="getInfo"
          />
        </template>

        <SessionListItem
          :sessions="sessions"
          :eventId="campaign.event_id"
          v-if="!campaign.calendarMode"
          @emitGetSessionStatus="getInfo"
        />
      </CustomCard>
      <CustomCard
        v-for="section in contentSections"
        :key="section.id"
        :id="section.id"
      >
        <template v-slot:title>
          <h4>{{ $t(section.titleKey) }}</h4>
        </template>
        <div
          v-html="section.html"
          class="campaign-content-frame text-left"
        ></div>
      </CustomCard>
    </CustomRow>

    <template v-if="campaign.isAdvertised && campaign.advertisement">
      <v-overlay :opacity="0.6" v-model="adModal.show" z-index="6">
        <v-dialog
          v-model="adModal.show"
          :width="475"
          :height="$vuetify.breakpoint.xs ? 400 : 533"
          :persistent="false"
          hide-overlay
        >
          <v-card class="text-center pa-5">
            <h2 class="mb-4">{{ adTitle }}</h2>
            <a :href="campaign.advertisement.href" target="_blank">
              <v-img
                :src="campaign.advertisement.img"
                :height="$vuetify.breakpoint.xs ? 302 : 435"
              />
            </a>
          </v-card>
          <v-btn
            icon
            @click="adModal.show = false"
            fab
            depressed
            small
            class="primary-1 float-btn"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-dialog>
      </v-overlay>
    </template>
  </div>
</template>

<script>
import SessionCalendar from "@/views/components/SessionCalendar";
import SessionListItem from "@/views/components/SessionListItem";
import { getEvent, getSessions } from "@/api/static";
import { mapActions } from "vuex";
import CC from "@/api/config-center";
import dayjs from "dayjs";
import { decrypt } from "../vendor/crypto";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"; // dependent on utc plugin
import { initGtag } from "@/shared/utils";
import { redirectToIndex } from "@/shared/redirectToIndex";
import { extractErrCode } from "@/shared/responsePayload";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Taipei");

export default {
  components: { SessionCalendar, SessionListItem },
  data() {
    const dt = new Date();
    const timeString = `${dt.getFullYear()}/${
      dt.getMonth() + 1
    }/${dt.getDate()} ${dt.getHours()}:${dt.getMinutes()}`;
    return {
      campaign: {},
      adModal: { show: true },
      tabSelect: null,
      _scrollTicking: false,
      _scrollHandler: null,
      sessions: [
        {
          address: "Loading...",
          areaAssignment: undefined,
          date: "2024-05-20 ~ 2024-12-31",
          eventId: "2f6431aac30afb722f615b9b95aabb0e",
          exposeEnd: "2030-12-31T23:59:00+08:00",
          exposeStart: "2024-05-20T10:00:00+08:00",
          hidden: false,
          loadingStatusFinished: true,
          location: "Loading...",
          name: "Loading...",
          picBigArea: "",
          picSmallArea: "",
          sessionId: "c18900a1d5f295218fe60b982d7ece96",
          sortedIndex: 1,
          status: "onsale",
          ticketArea: true,
          time: "19:00 ~ 23:59",
        },
      ],
      showTitleBanner: false,
      titleBarHeight: 0,
      cachedTargetPositions: [],
      appBarHeight:
        this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm ? 48 : 64,
      reloadCount: 0,
      clickGoTo: false,
      isLottery: false,
    };
  },
  watch: {
    campaign: {
      deep: true,
      handler(newVal) {
        if (newVal.title) {
          this.$nextTick(() => {
            this.titleBarHeight =
              document.getElementById("titleBar")?.clientHeight || 0;
          });
        }
      },
    },
  },
  metaInfo() {
    return {
      title: this.pageTitle,
      meta: [
        {
          property: "og:title",
          content: this.pageTitle,
          vmid: "og:title",
        },
        {
          property: "og:url",
          content: window.location.origin + window.location.pathname,
          vmid: "og:url",
        },
        {
          property: "og:img",
          content: this.campaign.picSmallActiveMain,
          vmid: "og:img",
        },
        {
          property: "og:image",
          content: this.campaign.picSmallActiveMain,
          vmid: "og:image",
        },
      ],
      link: [
        {
          rel: "canonical",
          href: window.location.origin + window.location.pathname,
        },
      ],
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: this.pageTitle,
            url: window.location.origin + window.location.pathname,
          },
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      return `${this.campaign.title || ""} - ${this.$t("app_name")}`;
    },
    lotterySuffix() {
      return this.campaign.isLottery ? "_lottery" : "";
    },
    announcementSection() {
      return this.campaign.announcement
        ? {
            id: "announcement",
            titleKey: "activity_announcement",
            html: this.campaign.announcement,
          }
        : null;
    },
    contentSections() {
      return [
        {
          id: "activityInfo",
          titleKey: "activity_intro",
          html: this.campaign.info,
        },
        {
          id: "notice",
          titleKey: `activity_notice${this.lotterySuffix}`,
          html: this.campaign.notice,
        },
        {
          id: "remind",
          titleKey: `activity_buy_notice${this.lotterySuffix}`,
          html: this.campaign.buy_notice,
        },
        {
          id: "getInfo",
          titleKey: "activity_get_notice",
          html: this.campaign.get_notice,
        },
        {
          id: "refundInfo",
          titleKey: "activity_refund_info",
          html: this.campaign.refund_info,
        },
      ].filter((section) => section.html);
    },
    tabs() {
      const tabList = [];
      if (this.announcementSection) {
        tabList.push({
          title: this.$t(this.announcementSection.titleKey),
          type: this.announcementSection.id,
        });
      }
      if (this.sessions.length) {
        tabList.push({
          title: this.$t(`activity_buy_ticket${this.lotterySuffix}`),
          type: "buyTicket",
        });
      }
      this.contentSections.forEach((section) => {
        tabList.push({
          title: this.$t(section.titleKey),
          type: section.id,
        });
      });
      return tabList;
    },
    adTitle() {
      return this.campaign.isAdvertised
        ? this.campaign.advertisement.title[this.$store.getters.locale]
        : null;
    },
    warningText() {
      return this.campaign.hasWarning
        ? this.campaign.warningText[this.$store.getters.locale]
        : null;
    },
  },
  created() {
    this.$store.dispatch("setOverlayLoading", true);
    this._scrollHandler = () => {
      if (!this._scrollTicking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          this._scrollTicking = false;
        });
        this._scrollTicking = true;
      }
    };
    window.addEventListener("scroll", this._scrollHandler);
    window.addEventListener("resize", this.updateTargetPositions);
  },
  async mounted() {
    const { data } = await getEvent(this.$route.params.id);
    if (this.shouldRedirectToIndex(data)) {
      return;
    }
    if (data && data.errCode && data.errCode !== "00") {
      this.$store.dispatch("setOverlayLoading", false);
      return;
    }
    this.campaign = {
      ...data,
      isLottery: !!data.lotteryDeadline,
    };

    if (!this.campaign.event_id) {
      redirectToIndex("activity-missing-event-id", {
        routeId: this.$route.params.id,
      });
      return;
    }

    const { data: sessionsData } = await getSessions(this.campaign.event_id);
    if (this.shouldRedirectToIndex(sessionsData)) {
      return;
    }
    const sessionList = Array.isArray(sessionsData && sessionsData.sessions)
      ? sessionsData.sessions
      : [];
    this.sessions = sessionList.map((s) => {
      s.loadingStatusFinished = false;
      return s;
    });
    await this.getInfo();

    const gaId = this.campaign.gaId || this.campaign.gaid;
    if (gaId) {
      initGtag(gaId);
    }
    this.$gtm.trackView(`Activity：${this.campaign.title}`, this.$route.path);
  },
  methods: {
    shouldRedirectToIndex(data) {
      const errCode = extractErrCode(data);
      if (Number(errCode) === 999 || String(errCode) === "102") {
        redirectToIndex("activity-guard-errCode", {
          errCode,
        });
        return true;
      }
      return false;
    },
    ...mapActions({
      showDialog: "dialog/show",
      setCampaign: "event/setCampaign",
    }),
    async getInfo() {
      let req = {
        eventId: [this.$route.params.id],
      };
      if (this.sessions.length) {
        req.sessionId = this.sessions.map((s) => s.sessionId);
      }
      const [infoResponse, getInfosErr] = await CC.getInfos(req);
      const info = infoResponse && infoResponse.data;

      if (
        this.shouldRedirectToIndex(info) ||
        this.shouldRedirectToIndex(
          getInfosErr && getInfosErr.response && getInfosErr.response.data
        )
      ) {
        return;
      }

      if (getInfosErr || !info) {
        if (this.reloadCount < 3) {
          this.sessions = this.sessions.map((s) => ({
            ...s,
            canLoadingAgain: true,
          }));
          this.reloadCount++;
          this.getInfo();
        } else {
          this.showDialog({
            alert: true,
            title: this.$t("activity_busy_title"),
            content: this.$t("activity_busy_content"),
          });
          this.$store.dispatch("setOverlayLoading", false);
        }
      } else {
        const { event, session } = info.result || {};
        if (Array.isArray(session) && session.length && !session[0].status) {
          if (this.reloadCount < 3) {
            this.sessions = this.sessions.map((s) => ({
              ...s,
              canLoadingAgain: true,
            }));
            this.reloadCount++;
            this.getInfo();
          } else {
            this.showDialog({
              alert: true,
              title: this.$t("activity_busy_title"),
              content: this.$t("activity_busy_content"),
            });
          }
        } else {
          await this.setEvent(Array.isArray(event) ? event[0] : {});
          if (Array.isArray(session)) await this.setSession(session);
        }
        this.appBarHeight =
          this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm ? 48 : 64;
        this.showTitleBanner = false;

        this.$store.dispatch("setOverlayLoading", false);
        this.$nextTick(() => this.updateTargetPositions());
      }
    },
    setEvent(event = {}) {
      this.campaign = {
        ...this.campaign,
        ...event,
      };
      this.setCampaign({
        ...this.campaign,
      });

      if (this.campaign.isAdvertised) {
        this.adModal = {
          show: true,
        };
      }

      if (this.campaign.hasWarning) {
        this.showDialog({
          title: this.warningText.title,
          content: this.warningText.content,
          buttons: [
            {
              text: this.$t("back_to_index"),
              style: {
                block: true,
                outlined: true,
                color: "primary",
              },
              action: async () => {
                this.$router.push({ name: "Index" });
              },
            },
            {
              text: this.$t("layout_notification_confirm"),
              style: {
                block: true,
                outlined: false,
                color: "primary",
              },
              action: () => {},
            },
          ],
        });
      }
    },
    setSession(sessions) {
      const normalizeSessionId = (id) =>
        /^s[0-9]{9}$/.test(id) ? id : decrypt(id);
      this.sessions = this.sessions.reduce((accr, s) => {
        const sid = normalizeSessionId(s.sessionId);
        const resSession =
          sessions.find((res_s) => normalizeSessionId(res_s.id) === sid) || {};

        accr.push({
          ...s,
          loadingStatusFinished: true,
          sortedIndex: resSession.sortedIndex || 999,
          status: resSession.status,
          areaAssignment: resSession.areaAssignment,
        });
        return accr;
      }, []);
      if (this.sessions.length && !!this.sessions[0].sortedIndex) {
        this.sessions.sort((a, b) => a.sortedIndex - b.sortedIndex);
      }
    },
    updateTargetPositions() {
      const campainTabsEl = document.getElementById("campainTabs");
      if (!campainTabsEl) return;
      const subHeight = campainTabsEl.clientHeight + this.titleBarHeight + 16;
      this.cachedTargetPositions = this.tabs.map(
        (t) => (document.getElementById(t.type)?.offsetTop || 0) - subHeight
      );
    },
    handleScroll() {
      // 處理滾動
      const curr_offset = window.scrollY.toFixed(0);

      // show titlebanner
      const bannerEl = document.getElementById("banner");
      if (
        bannerEl &&
        curr_offset > bannerEl.clientHeight - this.titleBarHeight
      ) {
        this.showTitleBanner = true;
      } else {
        this.showTitleBanner = false;
      }

      // prevent switch tabs by window scroll when clicking tab
      if (!this.clickGoTo) {
        const idx = this.cachedTargetPositions.findIndex(
          (p) => curr_offset < p
        );
        this.tabSelect =
          idx === -1 ? this.cachedTargetPositions.length - 1 : idx;
      }
    },
    scrollTo(target, tab) {
      this.clickGoTo = true;
      const targetEl = document.getElementById(target);
      const tabsEl = document.getElementById("campainTabs");
      const tHeight =
        (targetEl?.offsetTop || 0) -
        (tabsEl?.clientHeight || 0) -
        this.titleBarHeight -
        16;

      this.$vuetify.goTo(tHeight + this.appBarHeight, { duration: 300 });
      // 活動_${代入活動名稱}_${tab.title}tab
      this.$gtm.trackView(
        `Activity：${this.campaign.title}_${tab.title}`,
        this.$route.path
      );

      setTimeout(() => {
        this.clickGoTo = false;
      }, 350);
    },
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this._scrollHandler);
    window.removeEventListener("resize", this.updateTargetPositions);
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-slide-group__prev {
  &::before {
    content: "";
    display: block;
    border-right: 8px solid #1383b6;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid transparent;
    margin-left: -4px;
  }

  &.v-slide-group__prev--disabled::before {
    border-right: 8px solid rgba(0, 0, 0, 0.38) !important;
  }
  > * {
    display: none;
  }
}

::v-deep .v-slide-group__next {
  &::before {
    content: "";
    display: block;
    border-left: 8px solid #1383b6;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid transparent;
    margin-left: 4px;
  }

  &.v-slide-group__next--disabled::before {
    border-left: 8px solid rgba(0, 0, 0, 0.38) !important;
  }
  > * {
    display: none;
  }
}

.image {
  position: relative;
  width: 100%;
  border-radius: $radiusMobile;
  padding-bottom: 41.6666667%;
  background-color: $grey-lighten-1;
  overflow: hidden;

  img {
    position: absolute;
    height: 100%;
    position: absolute;
    top: 0; /* 補上：固定在頂部 */
    left: 0; /* 補上：固定在左側 */
    width: 100%; /* 補上：強制填滿寬度 */
    height: 100%; /* 你的原本設定：填滿高度 */
    object-fit: contain;
  }
}

::v-deep {
  .float-btn {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    margin-top: 20px;
  }

  @media screen and (max-width: 600px) {
    .location-content {
      max-height: 42px;
      overflow: hidden;
    }
  }

  .campaign-content-frame {
    ol,
    ul {
      margin: 0;
      padding: 0;
      counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8
        list-9;
    }

    ol,
    ul {
      padding-left: 1.5em;
    }

    ol > li,
    ul > li {
      list-style-type: none;
    }

    ul > li::before {
      content: "\2022";
    }

    ul[data-checked="false"],
    ul[data-checked="true"] {
      pointer-events: none;
    }

    ul[data-checked="false"] > li *,
    ul[data-checked="true"] > li * {
      pointer-events: all;
    }

    ul[data-checked="false"] > li::before,
    ul[data-checked="true"] > li::before {
      color: #777;
      cursor: pointer;
      pointer-events: all;
    }

    ul[data-checked="true"] > li::before {
      content: "\2611";
    }

    ul[data-checked="false"] > li::before {
      content: "\2610";
    }

    li::before {
      display: inline-block;
      white-space: nowrap;
      width: 1.2em;
    }

    li:not(.ql-direction-rtl)::before {
      margin-left: -1.5em;
      margin-right: 0.3em;
      text-align: center;
    }

    li.ql-direction-rtl::before {
      margin-left: 0.3em;
      margin-right: -1.5em;
    }

    ol > li:not(.ql-direction-rtl),
    ul li:not(.ql-direction-rtl) {
      padding-left: 1.5em;
    }

    ol > li.ql-direction-rtl,
    ul > li.ql-direction-rtl {
      padding-right: 1.5em;
    }

    ol > li {
      counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8
        list-9;
      counter-increment: list-0;
    }

    ol > li:before {
      content: counter(list-0, decimal) ". ";
    }

    ol > li.ql-indent-1 {
      counter-increment: list-1;
    }

    ol > li.ql-indent-1:before {
      content: counter(list-1, lower-alpha) ". ";
    }

    ol > li.ql-indent-1 {
      counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    }

    ol > li.ql-indent-2 {
      counter-increment: list-2;
    }

    ol > li.ql-indent-2:before {
      content: counter(list-2, lower-roman) ". ";
    }

    ol > li.ql-indent-2 {
      counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    }

    ol > li.ql-indent-3 {
      counter-increment: list-3;
    }

    ol > li.ql-indent-3:before {
      content: counter(list-3, decimal) ". ";
    }

    ol > li.ql-indent-3 {
      counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
    }

    ol > li.ql-indent-4 {
      counter-increment: list-4;
    }

    ol > li.ql-indent-4:before {
      content: counter(list-4, lower-alpha) ". ";
    }

    ol > li.ql-indent-4 {
      counter-reset: list-5 list-6 list-7 list-8 list-9;
    }

    ol > li.ql-indent-5 {
      counter-increment: list-5;
    }

    ol > li.ql-indent-5:before {
      content: counter(list-5, lower-roman) ". ";
    }

    ol > li.ql-indent-5 {
      counter-reset: list-6 list-7 list-8 list-9;
    }

    ol > li.ql-indent-6 {
      counter-increment: list-6;
    }

    ol > li.ql-indent-6:before {
      content: counter(list-6, decimal) ". ";
    }

    ol > li.ql-indent-6 {
      counter-reset: list-7 list-8 list-9;
    }

    ol > li.ql-indent-7 {
      counter-increment: list-7;
    }

    ol > li.ql-indent-7:before {
      content: counter(list-7, lower-alpha) ". ";
    }

    ol > li.ql-indent-7 {
      counter-reset: list-8 list-9;
    }

    ol > li.ql-indent-8 {
      counter-increment: list-8;
    }

    ol > li.ql-indent-8:before {
      content: counter(list-8, lower-roman) ". ";
    }

    ol > li.ql-indent-8 {
      counter-reset: list-9;
    }

    ol > li.ql-indent-9 {
      counter-increment: list-9;
    }

    ol > li.ql-indent-9:before {
      content: counter(list-9, decimal) ". ";
    }

    .ql-indent-1:not(.ql-direction-rtl) {
      padding-left: 3em;
    }

    li.ql-indent-1:not(.ql-direction-rtl) {
      padding-left: 4.5em;
    }

    .ql-indent-1.ql-direction-rtl.ql-align-right {
      padding-right: 3em;
    }

    li.ql-indent-1.ql-direction-rtl.ql-align-right {
      padding-right: 4.5em;
    }

    .ql-indent-2:not(.ql-direction-rtl) {
      padding-left: 6em;
    }

    li.ql-indent-2:not(.ql-direction-rtl) {
      padding-left: 7.5em;
    }

    .ql-indent-2.ql-direction-rtl.ql-align-right {
      padding-right: 6em;
    }

    li.ql-indent-2.ql-direction-rtl.ql-align-right {
      padding-right: 7.5em;
    }

    .ql-indent-3:not(.ql-direction-rtl) {
      padding-left: 9em;
    }

    li.ql-indent-3:not(.ql-direction-rtl) {
      padding-left: 10.5em;
    }

    .ql-indent-3.ql-direction-rtl.ql-align-right {
      padding-right: 9em;
    }

    li.ql-indent-3.ql-direction-rtl.ql-align-right {
      padding-right: 10.5em;
    }

    .ql-indent-4:not(.ql-direction-rtl) {
      padding-left: 12em;
    }

    li.ql-indent-4:not(.ql-direction-rtl) {
      padding-left: 13.5em;
    }

    .ql-indent-4.ql-direction-rtl.ql-align-right {
      padding-right: 12em;
    }

    li.ql-indent-4.ql-direction-rtl.ql-align-right {
      padding-right: 13.5em;
    }

    .ql-indent-5:not(.ql-direction-rtl) {
      padding-left: 15em;
    }

    li.ql-indent-5:not(.ql-direction-rtl) {
      padding-left: 16.5em;
    }

    .ql-indent-5.ql-direction-rtl.ql-align-right {
      padding-right: 15em;
    }

    li.ql-indent-5.ql-direction-rtl.ql-align-right {
      padding-right: 16.5em;
    }

    .ql-indent-6:not(.ql-direction-rtl) {
      padding-left: 18em;
    }

    li.ql-indent-6:not(.ql-direction-rtl) {
      padding-left: 19.5em;
    }

    .ql-indent-6.ql-direction-rtl.ql-align-right {
      padding-right: 18em;
    }

    li.ql-indent-6.ql-direction-rtl.ql-align-right {
      padding-right: 19.5em;
    }

    .ql-indent-7:not(.ql-direction-rtl) {
      padding-left: 21em;
    }

    li.ql-indent-7:not(.ql-direction-rtl) {
      padding-left: 22.5em;
    }

    .ql-indent-7.ql-direction-rtl.ql-align-right {
      padding-right: 21em;
    }

    li.ql-indent-7.ql-direction-rtl.ql-align-right {
      padding-right: 22.5em;
    }

    .ql-indent-8:not(.ql-direction-rtl) {
      padding-left: 24em;
    }

    li.ql-indent-8:not(.ql-direction-rtl) {
      padding-left: 25.5em;
    }

    .ql-indent-8.ql-direction-rtl.ql-align-right {
      padding-right: 24em;
    }

    li.ql-indent-8.ql-direction-rtl.ql-align-right {
      padding-right: 25.5em;
    }

    .ql-indent-9:not(.ql-direction-rtl) {
      padding-left: 27em;
    }

    li.ql-indent-9:not(.ql-direction-rtl) {
      padding-left: 28.5em;
    }

    .ql-indent-9.ql-direction-rtl.ql-align-right {
      padding-right: 27em;
    }

    li.ql-indent-9.ql-direction-rtl.ql-align-right {
      padding-right: 28.5em;
    }
  }
}

.rounded-rwd {
  border-radius: $radiusMobile;

  @media screen and (min-width: 769px) {
    border-radius: $radiusDesktop;
  }
}

.titleBanner {
  max-height: 5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.domain {
  height: 21px;
}

h1 {
  min-height: 60px;
}

.titleBar {
  position: fixed;
  width: 100%;
  z-index: 1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
}

.wordBreakKeepAll {
  word-break: keep-all;
}

.wordBreakAll {
  word-break: break-all;
}

@media screen and (max-width: 600px) {
  .sesstion-item {
    border: solid 2px $grey-lighten-1;
    border-radius: $radiusMobile;
    padding-left: 12px;
    padding-right: 12px;
    margin-bottom: 16px;
  }
  #buyTicket {
    min-height: 330px;
  }
}

.campainTabs {
  height: 42px;
  position: sticky;
  z-index: 1;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
}

::v-deep .v-tabs-bar {
  height: 42px !important;
  @media screen and (min-width: 769px) {
    height: 48px;
  }
}

::v-deep .v-tabs-slider-wrapper {
  height: 4px !important;
  background: $gradient !important;

  .v-tabs-slider {
    background: $gradient;
  }
}

::v-deep .campaign-content-frame {
  max-width: 970px;
  margin: 25px auto;
  font-weight: 500;

  iframe {
    width: 100%;
    height: calc(100vw * 0.5);
    max-height: 543px;
  }

  img {
    width: auto;
    max-width: 100%;
  }

  li {
    line-height: 2;
  }
}

.bg-yellow {
  background-color: #fff6d4;
}

#announcement {
  min-height: 280px;

  @media screen and (min-width: 768px) {
    min-height: 250px;
  }
}
</style>
